import React, {useMemo} from 'react';
import {Button, Icon, Text, StatusBadge, IconType, Tooltip} from '@/ui-kits';
import {FileItem, Consultation} from '@/interface';
import {ChatButton, FilePreview, ProgressBar} from '../../common';
import {
  ConsultationStatus,
  ConsultationTypeLabels,
  ConsultationStatusLabels,
  ConsultationAction,
  ConsultationStatusBadgeVariants,
  ReferralType,
  ConsultationType,
  ProgressBarSteps,
  LoaderBarType,
} from '@/constants';
import {cn, formatConsultationDate, formatName} from '@/utils/common_functions';
import {useDispatch, useSelector} from 'react-redux';
import {cancelCancelConsultationRequest, removeAdditionalConsultationFileRequest} from '@/services/redux/slices';
import {getPaymentReceipt} from '@/services/api';
import {CircleCheck} from 'lucide-react';
import {patientDashboardAdditionalFilesLoading, uploadAdditionalFilesMeta, cancelConsultationLoading, consultationList} from '@/services/redux/selectors';
import {useWindowDimensions} from '@/hooks';
import {accountDeletionStatusSelector} from '@/services/redux/selectors';
import {PatientAccountTicketRequestStatus} from '@/constants';

interface ConsultationCardProps {
  consultation: Consultation;
  onInteract: (consultation: Consultation, action: ConsultationAction, e?: React.MouseEvent<HTMLButtonElement>) => void;
  showFavouriteIcon?: boolean; // Show heart icon only in Past/History
}

import { addFavouriteDoctor, removeFavouriteDoctor } from '@/services/api';
import { useState, useEffect } from 'react';

export const ConsultationCard: React.FC<ConsultationCardProps> = ({consultation, onInteract, showFavouriteIcon = false}) => {
  // ==========================
  // hooks
  // ==========================
  const dispatch = useDispatch();
  const {width} = useWindowDimensions();

  // ==========================
  // selectors
  // ==========================
  const uploadAdditionalFilesMetaData = useSelector(uploadAdditionalFilesMeta);
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const uploadAdditionalFilesLoading = useSelector(patientDashboardAdditionalFilesLoading);
  const cancelLoadingMap = useSelector(cancelConsultationLoading);

  // ==========================
  // states & memoized values
  // ==========================
  const files = consultation.additional_files;

  // Local state for favorite toggle, sync with consultation.is_favourite
  const [isFavourite, setIsFavourite] = useState(!!consultation?.specialist?.is_favourite);
  const [favLoading, setFavLoading] = useState(false);

  
  // Add/remove favorite doctor
  const handleToggleFavourite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!consultation.specialist) return;
    setFavLoading(true);
    try {
      if (isFavourite) {
        await removeFavouriteDoctor({ specialist_id: consultation.specialist.id });
        setIsFavourite(false);
      } else {
        await addFavouriteDoctor({ specialist_id: consultation.specialist.id });
        setIsFavourite(true);
      }
    } catch (err) {
    } finally {
      setFavLoading(false);
    }
  };

  // state for follow up eligibility
  const isFollowUpEligible = useMemo(() => {
    if (consultation.status.id !== ConsultationStatus.ReportReady) return false;
    if (consultation.referral && consultation.referral.type === ReferralType.Internal) return false;
    if (!consultation.follow_up_date) return false;
    if (consultation.consultation_type !== ConsultationType['Online Second Opinion']) return false;
    if (new Date(consultation.follow_up_date) < new Date()) return false;
    if (accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) return false;
    return true;
  }, [consultation.status.id, consultation.referral?.type, consultation.follow_up_date, accountDeletionStatus]);

  // state for referral eligibility
  const isReferralEligible = useMemo(() => {
    if (![ConsultationStatus.ReportReady, ConsultationStatus.Completed].includes(consultation.status.id)) return false;
    if (!consultation?.referral) return false;
    if (consultation.referral.type === ReferralType.Internal && accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) return false;
    return true;
  }, [consultation.status.id, consultation.referral?.type, accountDeletionStatus]);

  // state for upload file loading and some derived states
  const uploadFileLoading = uploadAdditionalFilesMetaData[consultation.id.toString()] ?? {};
  const loadingIds = new Set(Object.keys(uploadFileLoading));
  const visibleFiles = files.filter(f => {
    if (loadingIds.has(f.id.toString()) && typeof uploadFileLoading[f.id] === 'object') {
      return false;
    }
    return true;
  });
  const isAnyFileUploading = useMemo(() => {
    return Object.values(uploadFileLoading).some(v => typeof v === 'object');
  }, [uploadFileLoading]);

  const isAnyFileDeleting = useMemo(() => {
    return Object.values(uploadFileLoading).some(v => v === true);
  }, [uploadFileLoading]);

  const actionButtonClass =
    '[320px]:w-auto sm:w-auto sm:min-w-0 sm:flex-shrink md:w-auto text-xs sm:text-sm md:text-xs lg:text-sm px-3 sm:px-4 md:px-3 lg:px-4 py-1.5 sm:py-2 md:py-1.5 lg:py-2 min-[481px]:px-4 min-[481px]:py-1.5';

  // ==========================
  // functions
  // ==========================
  function handleRemoveDoctorFile(fileId: number) {
    dispatch(
      removeAdditionalConsultationFileRequest({
        id: consultation.id,
        file_id: fileId,
      }),
    );
  }

  async function handleDownloadPaymentReceipt(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      const filename = `payment_receipt_case_${consultation.id}.pdf`;

      // Fetch blob directly from API (backend returns binary PDF)
      const blob = await getPaymentReceipt(consultation.id);

      // Create object URL and download
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.style.display = 'none';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      // Clean up the object URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading payment receipt:', error);
    }
  }
  // ==============================
  // side effects
  // ==============================
  
  useEffect(() => {
    if (consultation.id == 451){
      console.log(consultation)
    }
    setIsFavourite(!!consultation?.specialist?.is_favourite);
  }, [consultation.specialist?.is_favourite]);


  return (
    <div className="bg-white rounded-lg shadow-xl border patient-consulation-action-cta-container border-ui_input_border_dark/35 p-4 sm:p-6">
      {/* Case ID and Status */}
      <div className="flex flex-row flex-wrap items-center mb-4 sm:mb-6 border-b border-ui_input_border_dark/30 pb-3 sm:pb-4 gap-3 sm:gap-0">
        {consultation.hospital_poc && <Icon type="hospital_colorful" className="size-8 flex-shrink-0 mr-2" />}
        <h2 className="text-lg sm:text-xl md:text-lg lg:text-2xl font-FigtreeRegular text-ui_blue">
          Case ID : <span className="font-FigtreeBold">#{consultation.id}</span>
        </h2>

        <div className="flex items-center ml-3 mr-auto">
          {consultation?.linked_to && (
            <>
              <Icon type="link" className="size-5 flex-shrink-0" />
              <Text
                onClick={() => onInteract(consultation, ConsultationAction.LinkedTo)}
                variant="body"
                className="cursor-pointer text-ui_link !font-FigtreeMeduim">
                Linked to Case ID : #{consultation.linked_to.id}
              </Text>
            </>
          )}
        </div>

        <ConsultationStatusLabel status={consultation.status} className="" />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-[2fr_1.1fr_2fr_2fr] gap-5 sm:gap-7 lg:gap-8 mb-4 sm:mb-6">
        {/* specialist details */}
        {consultation.specialist && consultation.status.id !== ConsultationStatus.SpecialistAllocationPending && (
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-FigtreeMeduim text-ui_input_border_dark uppercase">SPECIALIST</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm md:text-sm lg:text-sm xl:text-base 2xl:text-base font-FigtreeBold text-ui_blue leading-tight">
                Dr. {formatName(consultation.specialist.name)}
              </span>
              {showFavouriteIcon && !consultation.hospital_poc && (
                <Tooltip message={isFavourite ? 'Remove from Favourites' : 'Add Doctor to Favourite'}>
                  <button
                    className="ml-1 focus:outline-none disabled:opacity-60"
                    onClick={handleToggleFavourite}
                    disabled={favLoading}
                    aria-label={isFavourite ? 'Remove from Favourites' : 'Add Doctor to Favourite'}
                  >
                    <Icon type={isFavourite ? 'heart_red' : 'heart_empty'} className="size-5 transition-all" />
                  </button>
                </Tooltip>
              )}
            </div>
            <p className="text-xs md:text-xs  font-FigtreeMedium text-ui_input_border_dark leading-tight mt-0.5">
              {consultation.specialist?.specialization?.label}
            </p>
          </div>
        )}

        {/* hospital poc details */}
        {consultation.hospital_poc && !consultation.specialist && (
          <div className="min-w-0">
            <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-1">HOSPITAL NAME</p>
            <p className="text-sm md:text-sm lg:text-sm xl:text-base 2xl:text-base font-FigtreeBold text-ui_blue leading-tight break-all">
              {formatName(consultation.hospital_poc?.name || '')}
            </p>
            <p className="text-xs md:text-xs  font-FigtreeMedium text-ui_input_border_dark leading-tight mt-0.5">{consultation.category?.label}</p>
          </div>
        )}
        <div className="min-w-0">
          <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-1">PAYMENT</p>
          <div className="flex items-center gap-2">
            <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeBold text-ui_blue leading-tight">₹{consultation.amount}</p>
            {/* consultation.amount ? (
              <button onClick={handleDownloadPaymentReceipt} className="cursor-pointer hover:opacity-70 transition-opacity" title="Download Payment Receipt">
                <Icon type="download_btn" className="size-5 sm:size-6 xl:size-7 flex-shrink-0" />
              </button>
            ) : null */}
          </div>
        </div>
        <div className="min-w-0 flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-1">CONSULTATION BOOKED</p>
            <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-ui_blue leading-tight">
              {formatConsultationDate(consultation.created_at)}
            </p>
          </div>
          <div className="relative group sm:hidden flex-shrink-0 ml-2">
            <div className="p-[10px]">
              {consultation.status.id !== ConsultationStatus.AwaitingDoctorReview &&
                consultation.chat_active &&
                accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
                  <ChatButton size="sm" onClick={e => onInteract(consultation, ConsultationAction.Chat, e)} chatId={consultation.id} />
                )}
            </div>
          </div>
        </div>
        <div className="min-w-0 flex items-start justify-between">
          <div className="min-w-0 flex-1">
            <p className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-1">CONSULTATION TYPE</p>
            <p className="text-sm md:text-xs lg:text-sm xl:text-base 2xl:text-base font-FigtreeSemiBold text-ui_blue leading-tight">
              {ConsultationTypeLabels[consultation.consultation_type]}
            </p>
          </div>
          <div className="relative group sm:hidden flex-shrink-0 ml-2">
            <Tooltip message="View Details">
              <button
                onClick={() => onInteract(consultation, ConsultationAction.View)}
                className="p-2 sm:p-3 bg-white text-ui_blue rounded-lg hover:bg-ui_blue_light/10 transition-colors">
                <Icon type="view_details" className="size-7 sm:size-9" />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row sm:flex-nowrap items-stretch sm:items-center gap-2 lg:gap-4 mb-3 sm:mb-6">
        {/* View Details Button - Show on left for tablet and larger screens */}
        <div className="hidden sm:block flex-shrink-0 self-center">
          <div className="relative group flex items-center flex-shrink-0">
            <Tooltip message="View Details">
              <button
                onClick={() => onInteract(consultation, ConsultationAction.View)}
                className="bg-white text-ui_blue rounded-lg hover:bg-ui_blue_light/10 transition-colors">
                <Icon type="view_details" className="size-7 sm:size-9 flex-shrink-0" />
              </button>
            </Tooltip>
          </div>
        </div>

        {[ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.SpecialistAllocationPending, ConsultationStatus.UnderReviewByDoctor].includes(
          consultation.status.id,
        ) && (
          <Button
            title="Upload Additional Document"
            variant="primary"
            disabled={isAnyFileUploading || accountDeletionStatus === PatientAccountTicketRequestStatus.Approved}
            className={actionButtonClass}
            textClassName="text-nowrap"
            onClick={() => onInteract(consultation, ConsultationAction.Upload)}
          />
        )}
        {[ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.SpecialistAllocationPending].includes(consultation.status.id) && (
          <Button
            title="Cancel Consultation"
            variant="secondary"
            disabled={accountDeletionStatus === PatientAccountTicketRequestStatus.Approved || cancelLoadingMap[consultation.id.toString()]}
            isLoading={cancelLoadingMap[consultation.id.toString()]}
            className={actionButtonClass}
            onClick={() => onInteract(consultation, ConsultationAction.Cancel)}
          />
        )}

        {[ConsultationStatus.ReportReady, ConsultationStatus.Completed].includes(consultation.status.id) && (
          <Button
            title="View Second Opinion Report"
            textClassName="!text-nowrap"
            variant={consultation.status.id === ConsultationStatus.ReportReady ? 'secondary' : 'primary'}
            className={actionButtonClass}
            onClick={() => onInteract(consultation, ConsultationAction.Report)}
          />
        )}

        {isFollowUpEligible && (
          <Button
            title="Follow-Up"
            variant="secondary"
            className={cn(actionButtonClass)}
            onClick={() => onInteract(consultation, ConsultationAction.FollowUp)}
          />
        )}

        {consultation.status.id === ConsultationStatus.ReportReady && (
          <>
            {consultation.feedback_submitted ? (
              <div className={`flex items-center f-r-lg justify-center border gap-1.5 sm:gap-2 border-ui_blue rounded-md ${actionButtonClass}`}>
                <Text variant="body" className="gradient-text text-nowrap font-FigtreeSemiBold !font-semibold !text-xs sm:!text-sm md:!text-xs lg:!text-sm">
                  Feedback Submitted
                </Text>
                <CircleCheck className="size-3 sm:size-4 text-ui_input_border_dark" />
              </div>
            ) : (
              <Button
                title="Feedback & Rating"
                variant="secondary"
                className={cn(actionButtonClass, 'f-r-lg')}
                onClick={() => onInteract(consultation, ConsultationAction.Feedback)}
              />
            )}
          </>
        )}

        {isReferralEligible && (
          <Button
            title="View Referral Details"
            variant={'secondary'}
            className={cn(actionButtonClass, consultation.status.id === ConsultationStatus.ReportReady && 'v-r-lg')}
            onClick={() => onInteract(consultation, ConsultationAction.Referral)}
          />
        )}

        {consultation.status.id !== ConsultationStatus.AwaitingDoctorReview &&
          consultation.chat_active &&
          accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
            <ChatButton
              className={consultation.status.id === ConsultationStatus.UnderReviewByDoctor && width >= 480 ? '' : 'c-lg'}
              onClick={e => onInteract(consultation, ConsultationAction.Chat, e)}
              chatId={consultation.id}
            />
          )}
      </div>

      {/* next row of action button for small screen */}
      {[ConsultationStatus.ReportReady, ConsultationStatus.Completed, ConsultationStatus.UnderReviewByDoctor].includes(consultation.status.id) && (
        <div className="flex flex-col -mt-1 sm:-mt-4 sm:flex-row items-stretch sm:items-center gap-2 lg:gap-4 mb-3 sm:mb-6">
          {/* Spacer to align with View Details icon button above - matches the icon container width */}
          <div className="hidden sm:block flex-shrink-0 self-center">
            <div className="w-[36px]"></div>
          </div>
          {consultation.status.id === ConsultationStatus.ReportReady && (
            <>
              {consultation.feedback_submitted ? (
                <div className={`flex items-center justify-center border gap-1.5 sm:gap-2 border-ui_blue rounded-md f-r-sm ${actionButtonClass}`}>
                  <Text variant="body" className="gradient-text font-FigtreeSemiBold !font-semibold !text-xs sm:!text-sm md:!text-xs lg:!text-sm">
                    Feedback Submitted
                  </Text>
                  <CircleCheck className="size-3 sm:size-4 text-ui_input_border_dark" />
                </div>
              ) : (
                <Button
                  title="Feedback & Rating"
                  variant="secondary"
                  className={cn(actionButtonClass, 'f-r-sm')}
                  onClick={() => onInteract(consultation, ConsultationAction.Feedback)}
                />
              )}
            </>
          )}
          {isReferralEligible && (
            <Button
              title="View Referral Details"
              variant={'secondary'}
              className={cn(actionButtonClass, 'v-r-sm')}
              onClick={() => onInteract(consultation, ConsultationAction.Referral)}
            />
          )}

          {consultation.status.id !== ConsultationStatus.AwaitingDoctorReview &&
            consultation.chat_active &&
            accountDeletionStatus !== PatientAccountTicketRequestStatus.Approved && (
              <ChatButton
                className={consultation.status.id === ConsultationStatus.UnderReviewByDoctor ? 'hidden' : 'c-sm'}
                onClick={e => onInteract(consultation, ConsultationAction.Chat, e)}
                chatId={consultation.id}
              />
            )}
        </div>
      )}

      {/* Uploaded Files Section - Show files only when status is awaiting */}
      <div className="flex flex-col gap-1.5 sm:gap-2 mb-3 sm:mb-5 max-w-full sm:max-w-[75%]">
        {[ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.SpecialistAllocationPending, ConsultationStatus.UnderReviewByDoctor].includes(
          consultation.status.id,
        ) &&
          visibleFiles.map(file => (
            <FilePreview
              bgVariant="blue"
              allowed_actions={['delete']}
              disabled={isAnyFileUploading}
              key={file.id}
              file={file}
              handleDelete={() => handleRemoveDoctorFile(file.id)}
              loading={typeof uploadFileLoading[file.id] === 'boolean'}
            />
          ))}
        {[ConsultationStatus.AwaitingDoctorReview, ConsultationStatus.SpecialistAllocationPending, ConsultationStatus.UnderReviewByDoctor].includes(
          consultation.status.id,
        ) &&
          Object.entries(uploadFileLoading).map(([key, value]) => {
            // only render when loading state has skeleton loading content
            if (typeof value === 'boolean') return;
            return (
              <FilePreview
                bgVariant="blue"
                allowed_actions={['delete']}
                key={key}
                file={value}
                disabled={true}
                handleDelete={() => handleRemoveDoctorFile(value.id)}
                loading={true}
              />
            );
          })}
        {((isAnyFileUploading && typeof uploadAdditionalFilesLoading === 'number' && uploadAdditionalFilesLoading !== ProgressBarSteps.NotStarted) ||
          isAnyFileDeleting) && (
          <div className="mt-4 flex justify-center w-full max-w-[600px]">
            <ProgressBar
              className="items-start"
              message={isAnyFileDeleting ? 'Deleting document...' : 'AI is analyzing your documents...'}
              loaderType={LoaderBarType.PatientConsultation}
              currentStep={1}
              onComplete={() => {}}
            />
          </div>
        )}
      </div>

      <DoctorUploadedFilesSection consultation={consultation} handleView={() => {}} />

      {/* Info Message */}
      <InfoMessage consultation={consultation} />
    </div>
  );
};

const InfoMessage: React.FC<{consultation: Consultation}> = props => {
  const {consultation} = props;
  const accountDeletionStatus = useSelector(accountDeletionStatusSelector);
  const infoMessages = {
    [ConsultationStatus.SpecialistAllocationPending]: {
      message:
        'You can still upload documents until your doctor starts reviewing the case. Once a doctor is assigned, your case will be reviewed within 48 hours.',
      bgColor: '#F1FBFF',
      borderColor: '#447DA8',
      textColor: '#486479',
      icon: 'file_arrow_up_blue',
    },
    [ConsultationStatus.AwaitingDoctorReview]: {
      message: consultation.hospital_poc ? (
        <span>
          Dr. <b className="font-FigtreeBold">{formatName(consultation.specialist?.name || '')}</b> from{' '}
          <b className="font-FigtreeBold">{formatName(consultation.hospital_poc?.name || '')}</b> has been assigned to review your case. You can upload
          additional documents here.
        </span>
      ) : (
        'You may upload additional documents until the second opinion is given.'
      ),
      bgColor: '#FFFAE5',
      borderColor: '#FFD633',
      textColor: '#39341F',
      icon: 'file_arrow_up',
    },
    [ConsultationStatus.UnderReviewByDoctor]: {
      message: consultation.hospital_poc ? (
        <span>
          Dr. <b className="font-FigtreeBold">{formatName(consultation.specialist?.name || '')}</b> from{' '}
          <b className="font-FigtreeBold">{formatName(consultation.hospital_poc?.name || '')}</b> has started reviewing your case. Expect your second opinion
          report within 48 hours.
        </span>
      ) : (
        `Dr. ${formatName(consultation?.specialist?.name || '')} has started reviewing your case. Expect your second opinion report within 48 hours.`
      ),
      bgColor: '#FFF5E7',
      borderColor: '#FFAF46',
      textColor: '#1A1918',
      icon: 'box_search',
    },
    [ConsultationStatus.ReportReady]: {
      message: `Please note: Your ${
        consultation?.referral && consultation.referral.type === ReferralType.Internal ? 'referral' : 'follow-up'
      } is available until ${formatConsultationDate(
        consultation?.referral && consultation.referral.type === ReferralType.Internal
          ? consultation?.referral_expiry_date || ''
          : consultation?.follow_up_date || '',
        'short',
      )}.`,
      bgColor: '#E5FFE2',
      borderColor: '#198F51',
      textColor: '#145734',
      icon: 'calender_green',
    },
  };

  // if status mismatch, return null
  if (
    ![
      ConsultationStatus.AwaitingDoctorReview,
      ConsultationStatus.UnderReviewByDoctor,
      ConsultationStatus.ReportReady,
      ConsultationStatus.SpecialistAllocationPending,
    ].includes(consultation.status.id)
  ) {
    return null;
  }

  // if report ready and consultation type is follow up or referral (including hospital types), return null
  if (
    consultation.status.id === ConsultationStatus.ReportReady &&
    [ConsultationType['Follow Up Second Opinion'], ConsultationType['Referral Second Opinion'], ConsultationType['Hospital Follow Up Second Opinion'], ConsultationType['Hospital Referral Second Opinion']].includes(consultation.consultation_type)
  ) {
    return null;
  }

  if (consultation.status.id === ConsultationStatus.ReportReady && accountDeletionStatus === PatientAccountTicketRequestStatus.Approved) {
    return null;
  }

  return (
    <div
      className={cn('border-l-4 rounded-lg p-2 sm:p-4 flex items-center gap-2 sm:gap-3')}
      style={{
        backgroundColor: infoMessages[consultation.status.id as keyof typeof infoMessages].bgColor,
        borderColor: infoMessages[consultation.status.id as keyof typeof infoMessages].borderColor,
      }}>
      <Icon
        type={infoMessages[consultation.status.id as keyof typeof infoMessages].icon as IconType}
        className="size-4 sm:size-5 text-white flex-shrink-0 mt-0.5"
      />
      <p
        style={{color: infoMessages[consultation.status.id as keyof typeof infoMessages].textColor}}
        className="text-xs sm:text-sm md:text-xs lg:text-sm font-FigtreeMeduim text-white">
        {infoMessages[consultation.status.id as keyof typeof infoMessages].message}
      </p>
    </div>
  );
};

const ConsultationStatusLabel: React.FC<{
  status: Consultation['status'];
  className?: string;
}> = props => {
  const {status, className} = props;

  return (
    <StatusBadge className={className} status={ConsultationStatusBadgeVariants[status.id]}>
      {ConsultationStatusLabels[status.id]}
    </StatusBadge>
  );
};

const DoctorUploadedFilesSection: React.FC<{
  consultation: Consultation;
  handleView: (file: FileItem) => void;
}> = ({consultation, handleView}) => {
  if (!consultation?.doctor_files) return null;

  if (consultation.status.id !== ConsultationStatus.ReportReady || consultation.doctor_files?.length === 0) {
    return null;
  }
  return (
    <>
      <Text className="text-xs font-FigtreeMedium text-ui_input_border_dark uppercase mb-1">Doctor Shared Files ({consultation.doctor_files.length})</Text>
      {/* doctors upload file sections - Show files only when status is awaiting */}
      {
        <div className="flex flex-col gap-1.5 sm:gap-2 mb-3 sm:mb-5 max-w-full sm:max-w-[75%]">
          {[ConsultationStatus.ReportReady, ConsultationStatus.Completed].includes(consultation.status.id) &&
            consultation.doctor_files.map(file => (
              <FilePreview readonly key={file.id.toString()} bgVariant="blue" allowed_actions={['download']} file={file} handleView={handleView} />
            ))}
        </div>
      }
    </>
  );
};
